import type { ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  LogOut,
  RefreshCw,
  type LucideIcon,
  List,
} from 'lucide-react';
import { auth } from '@/lib/firebase';
import { useCurrentUser } from '@/lib/use-current-user';
import type { TournamentStatus, MatchStatus } from '@/data/types';

/* ─── Initials helper ─────────────────────────────────────────────────────── */
export function initials(name: string): string {
  return name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/* ─── App Header ──────────────────────────────────────────────────────────── */
interface AppHeaderProps {
  /** Show the signed-in user chip with logout. */
  showUser?: boolean;
  /** Extra content rendered on the right (before the user chip). */
  right?: ReactNode;
}

export function AppHeader({ showUser = false, right }: AppHeaderProps) {
  const user = useCurrentUser();

  return (
    <header className="app-header">
      <div className="container-d app-header-inner">
        <Link to="/" className="brand">
          <span className="brand-mark">T</span>
          <span>Turneo</span>
        </Link>
        <div className="row gap-3">
          {right}
          {showUser && user && (
            <div className="user-chip">
              <span className="t-small">{user.email}</span>
              <div className="user-avatar" title={user.displayName ?? user.email ?? ''}>
                {initials(user.displayName ?? user.email ?? '?')}
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void signOut(auth)}
                title="Выйти"
                style={{ height: 28, padding: '0 6px' }}
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─── Status badge ────────────────────────────────────────────────────────── */
const STATUS_VARIANT: Record<TournamentStatus, string> = {
  DRAFT: 'draft',
  READY: 'ready',
  IN_PROGRESS: 'live',
  COMPLETED: 'done',
};
const STATUS_LABEL: Record<TournamentStatus, string> = {
  DRAFT: 'Черновик',
  READY: 'Готов',
  IN_PROGRESS: 'Идёт',
  COMPLETED: 'Завершён',
};

export function StatusBadge({ status }: { status: TournamentStatus }) {
  return (
    <span className={`badge badge-${STATUS_VARIANT[status]}`}>
      {status === 'IN_PROGRESS' ? <span className="live-dot" /> : <span className="dot" />}
      {STATUS_LABEL[status]}
    </span>
  );
}

/* ─── Match status badge ──────────────────────────────────────────────────── */
const MATCH_STATUS: Record<MatchStatus, { v: string; label: string }> = {
  PENDING: { v: 'draft', label: 'Ожидает' },
  READY: { v: 'ready', label: 'Готов к игре' },
  IN_PROGRESS: { v: 'warn', label: 'Идёт' },
  COMPLETED: { v: 'done', label: 'Сыгран' },
};

export function MatchStatusBadge({ status }: { status: MatchStatus }) {
  const c = MATCH_STATUS[status];
  return (
    <span className={`badge badge-${c.v}`}>
      <span className="dot" />
      {c.label}
    </span>
  );
}

/* ─── Place badge ─────────────────────────────────────────────────────────── */
export function PlaceBadge({
  place,
  size = 'default',
}: {
  place?: string | null;
  size?: 'default' | 'sm';
}) {
  if (!place || place === '—') return null;
  let cls = 'badge badge-place';
  if (place === '1') cls += ' badge-place-1';
  else if (place === '2') cls += ' badge-place-2';
  else if (place === '3' || place === '3-4') cls += ' badge-place-3';
  return (
    <span
      className={cls}
      style={size === 'sm' ? { height: 18, padding: '0 6px', fontSize: 10 } : undefined}
    >
      {place}
    </span>
  );
}

/* ─── Empty / loading / error states ──────────────────────────────────────── */
export function EmptyState({
  icon: IconCmp = List,
  title,
  body,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <IconCmp size={20} />
      </div>
      <div className="empty-title">{title}</div>
      {body && <div className="empty-body">{body}</div>}
      {action}
    </div>
  );
}

export function LoadingState({ label = 'Загрузка...' }: { label?: string }) {
  return (
    <div className="empty">
      <div className="spinner spinner-lg" style={{ marginBottom: 14 }} />
      <div className="empty-title" style={{ fontWeight: 500, color: 'var(--fg-muted)' }}>
        {label}
      </div>
    </div>
  );
}

export function ErrorState({
  title = 'Что-то пошло не так',
  body,
  onRetry,
}: {
  title?: string;
  body?: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="empty">
      <div
        className="empty-icon"
        style={{ color: 'var(--red)', background: 'var(--red-soft)', borderColor: 'var(--red-soft)' }}
      >
        <AlertCircle size={20} />
      </div>
      <div className="empty-title">{title}</div>
      {body && <div className="empty-body">{body}</div>}
      {onRetry && (
        <button className="btn" onClick={onRetry}>
          <RefreshCw size={14} /> Попробовать снова
        </button>
      )}
    </div>
  );
}
